import React, { ReactElement, useContext } from 'react'

import Fieldset from '../../Common/Components/Fieldset'
import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import TextInput from '../../Common/Components/TextInput'
import { Select, SelectOptions, SelectRange, SelectRangeItem } from '../../Common/Components/Selects'

import { GenericFilterEntityItem, RptFilterEntityInfo } from '../../Common/Types/Types'
import { ANY_VALUE_NUMBER_NUMERIC } from '../../Common/Constants/DefaultValues'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CallMovementFiltering as CallMovementFilteringLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { TELEPHONY_SYSTEM_TYPE_MS_TEAMS } from '../Constants'

// CSS imports. Commented out because of current build process.
// import './callMovementFiltering.scss'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telnosMovedTo: string
    setTelnosMovedTo: (newValue: string) => void
    devicesMovedFrom: string
    setDevicesMovedFrom: (newValue: string) => void
    ACDAgentsMovedFrom: string
    setACDAgentsMovedFrom: (newValue: string) => void
    callSegmentNumber: number
    setCallSegmentNumber: (newValue: number) => void
    callSegmentStartReason: number
    setCallSegmentStartReason: (newValue: number) => void
    callSegmentEndReason: number
    setCallSegmentEndReason: (newValue: number) => void
    callSegmentChainID: string
    setCallSegmentChainID: (newValue: string) => void
    isReportTemplateUser: boolean
    currentTelSysType?: number
}

/**
 * Call movement filtering section in "Other" tab.
 *
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telnosMovedTo Report property.
 * @param setTelnosMovedTo Setter for corresponding report property.
 * @param devicesMovedFrom Report property.
 * @param setDevicesMovedFrom Setter for corresponding report property.
 * @param ACDAgentsMovedFrom Report property.
 * @param setACDAgentsMovedFrom Setter for corresponding report property.
 * @param callSegmentNumber Report property.
 * @param setCallSegmentNumber Setter for corresponding report property.
 * @param callSegmentStartReason Report property.
 * @param setCallSegmentStartReason Setter for corresponding report property.
 * @param callSegmentEndReason Report property.
 * @param setCallSegmentEndReason Setter for corresponding report property.
 * @param callSegmentChainID Report property.
 * @param setCallSegmentChainID Setter for corresponding report property.
 */
const CallMovementFiltering: React.FC<Props> = ({
    filterEntityInfo,
    telnosMovedTo,
    setTelnosMovedTo,
    devicesMovedFrom,
    setDevicesMovedFrom,
    ACDAgentsMovedFrom,
    setACDAgentsMovedFrom,
    callSegmentNumber,
    setCallSegmentNumber,
    callSegmentStartReason,
    setCallSegmentStartReason,
    callSegmentEndReason,
    setCallSegmentEndReason,
    callSegmentChainID,
    setCallSegmentChainID,
    isReportTemplateUser,
    currentTelSysType
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('CallMovementFiltering')

    const callSegmentNumberOptions: SelectOptions<number> = [
        {
            value: ANY_VALUE_NUMBER_NUMERIC,
            label: language.get(CallMovementFilteringLanguage.CallsegmentnumberAnyOptionLabel)
        },
        {
            value: 1,
            label: language.get(CallMovementFilteringLanguage.CallsegmentnumberFirstOptionLabel)
        },
        {
            value: 2,
            label: language.get(CallMovementFilteringLanguage.Callsegmentnumber2OptionLabel)
        },
        {
            value: 3,
            label: language.get(CallMovementFilteringLanguage.Callsegmentnumber3OptionLabel)
        },
        {
            value: 4,
            label: language.get(CallMovementFilteringLanguage.Callsegmentnumber4OptionLabel)
        },
        {
            value: 5,
            label: language.get(CallMovementFilteringLanguage.Callsegmentnumber5OptionLabel)
        },
        {
            value: -106,
            label: language.get(CallMovementFilteringLanguage.Callsegmentnumber6plusOptionLabel)
        },
        {
            value: -2,
            label: language.get(CallMovementFilteringLanguage.CallsegmentnumberLastOptionLabel)
        }
    ]

    const callSegmentStartReasonOptions: SelectOptions<number> = [
        {
            value: ANY_VALUE_NUMBER_NUMERIC,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonAnyOptionLabel)
        },
        {
            value: 0,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonDivertedtoOptionLabel)
        },
        {
            value: 1,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonOverflowedinOptionLabel)
        },
        ...(currentTelSysType === TELEPHONY_SYSTEM_TYPE_MS_TEAMS
            ? []
            : [
                  {
                      value: 2,
                      label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonAdvancedtoOptionLabel)
                  }
              ]),
        {
            value: 3,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonTransferredtoOptionLabel)
        },
        {
            value: 4,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonConferencedtoOptionLabel)
        },
        {
            value: 5,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonConferencedegenOptionLabel)
        },
        {
            value: 6,
            label: language.get(CallMovementFilteringLanguage.CallsegmentstartreasonEscapedtoOptionLabel)
        }
    ]

    const callSegmentEndReasonOptions: SelectOptions<number> = [
        {
            value: ANY_VALUE_NUMBER_NUMERIC,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonAnyOptionLabel)
        },
        {
            value: 0,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonDivertedfromOptionLabel)
        },
        {
            value: 1,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonOverflowedoffOptionLabel)
        },
        ...(currentTelSysType === TELEPHONY_SYSTEM_TYPE_MS_TEAMS
            ? []
            : [
                  {
                      value: 2,
                      label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonAdvancedfromOptionLabel)
                  }
              ]),
        {
            value: 3,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonTransferredfromOptionLabel)
        },
        {
            value: 4,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonConferencedfromOptionLabel)
        },
        {
            value: 5,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonConferencedegenOptionLabel)
        },
        {
            value: 6,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonEscapedfromOptionLabel)
        },
        {
            value: 10,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonConsultationtrtoOptionLabel)
        },
        {
            value: 20,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonMovedtoconferenceOptionLabel)
        },
        {
            value: 21,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonMovedtoparkOptionLabel)
        },
        {
            value: 22,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonMovedtovoicemailOptionLabel)
        },
        {
            value: 11,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonEndedonholdonlyOptionLabel)
        },
        {
            value: 111,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonEndedonholdexclOptionLabel)
        },
        {
            value: 23,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonUnmonitoreddestOptionLabel)
        },
        {
            value: 24,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonUnknowndestOptionLabel)
        },
        {
            value: 28,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonAllunrslvddestonlyOptionLabel)
        },
        {
            value: 128,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonAllunrslvddestexclOptionLabel)
        },
        {
            value: 30,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonModellingerrorsonlyOptionLabel)
        },
        {
            value: 130,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonModellingerrorsexclOptionLabel)
        },
        {
            value: 31,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonApplicationendedOptionLabel)
        },
        {
            value: 32,
            label: language.get(CallMovementFilteringLanguage.CallsegmentendreasonDevicedeletedOptionLabel)
        },
        {
            value: 33,
            label: language.get(
                CallMovementFilteringLanguage.CallsegmentendreasonNoreplyneededEmailsexcludedOptionLabel
            )
        }
    ]

    const toEntityOptions = (entityContainerItem: GenericFilterEntityItem | undefined): SelectRangeItem[] =>
        entityContainerItem
            ? entityContainerItem.Data.map((entity) => ({
                  value: entity.ID,
                  renderElement: (
                      <div>
                          {entity.ID} - {entity.Description}
                      </div>
                  )
              }))
            : []

    return (
        <Fieldset className={'call-movement-filtering'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CallMovementFilteringLanguage.CallMovementFiltering_SectionHeader)}
            </Heading>
            {!isReportTemplateUser ? (
                <>
                    <FormRow className={'telnos-moved-to'}>
                        <TextInput
                            value={telnosMovedTo}
                            onChange={setTelnosMovedTo}
                            labelLeft={language.get(CallMovementFilteringLanguage.TelnosmovedtoLabel)}
                            placeholder={language.get(
                                CallMovementFilteringLanguage.CallMovementFiltering_AnyPlaceholder
                            )}
                        />
                    </FormRow>
                    <FormRow className={'devices-moved-from'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.Devices)}
                            rangeExpressionValue={devicesMovedFrom}
                            onValueChange={setDevicesMovedFrom}
                            specialAnyItemIndex={0}
                            labelLeft={language.get(CallMovementFilteringLanguage.DevicesmovedfromLabel)}
                        />
                    </FormRow>
                    <FormRow className={'acd-agents-moved-from'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.ACDAgents)}
                            rangeExpressionValue={ACDAgentsMovedFrom}
                            onValueChange={setACDAgentsMovedFrom}
                            specialAnyItemIndex={0}
                            labelLeft={language.getWithMSTCheck(
                                CallMovementFilteringLanguage.AcdagentsmovedfromLabel,
                                currentTelSysType
                            )}
                        />
                    </FormRow>
                </>
            ) : (
                <></>
            )}
            <FormRow className={'call-segment-number'}>
                <Select
                    label={language.get(CallMovementFilteringLanguage.CallsegmentnumberLabel)}
                    value={callSegmentNumber}
                    handleChange={setCallSegmentNumber}
                    options={callSegmentNumberOptions}
                />
            </FormRow>
            <FormRow className={'call-segment-start-reason'}>
                <Select
                    label={language.get(CallMovementFilteringLanguage.CallsegmentstartreasonLabel)}
                    value={callSegmentStartReason}
                    handleChange={setCallSegmentStartReason}
                    options={callSegmentStartReasonOptions}
                />
            </FormRow>
            <FormRow className={'call-segment-end-reason'}>
                <ComboBoxAlt
                    label={language.get(CallMovementFilteringLanguage.CallsegmentendreasonLabel)}
                    value={callSegmentEndReason}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            setCallSegmentEndReason(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            setCallSegmentEndReason(newValue[0])
                        }
                    }}
                    options={callSegmentEndReasonOptions}
                />
            </FormRow>
            {!isReportTemplateUser ? (
                <FormRow className={'call-segment-chain-id'}>
                    <TextInput
                        value={callSegmentChainID}
                        onChange={setCallSegmentChainID}
                        labelLeft={language.get(CallMovementFilteringLanguage.CallsegmentchainidLabel)}
                        placeholder={language.get(CallMovementFilteringLanguage.CallMovementFiltering_AnyPlaceholder)}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </Fieldset>
    )
}

export default React.memo(CallMovementFiltering)
